import { logInDev } from './log-utils';
import {
  NAME,
  USERNAME,
  USER_ROLE,
  BRAND_NAME,
  AUTH_TOKEN,
  CURRENT_USER,
  ORGANIZATION_ID,
  SANDBOX_VALIDITY,
  USER_BRAND_PIC_URL,
  SHOW_COLLAPSED_NAV,
  ACTIVE_IMPERSONATOR,
  ORGANIZATION_USER_ID,
  RAZORPAY_PROFILE_NAME,
  MOBILE_UNSUPPORTED_ROUTES_EXCLUSION_LIST,
  AGENT_SIDEBAR_OPEN,
  AGENT_CURRENT_CONVERSATION_ID,
  JWT_TOKEN,
  JWT_TOKEN_EXPIRY,
} from '../constants/session-storage-keys';

const setInSessionStorage = (key, value, stringifyValue = true) => {
  if (stringifyValue) {
    sessionStorage.setItem(key, JSON.stringify(value));
  } else {
    sessionStorage.setItem(key, value);
  }
};

const getUserObjectFromSessionStorage = () => {
  const currentUser = sessionStorage.getItem(CURRENT_USER);

  if (currentUser) {
    return JSON.parse(decodeURIComponent(currentUser));
  }

  return {};
};

// Returns true only when the login response (persisted as CURRENT_USER)
// carries both tenantId and authUserId. Used to gate the xauth-token JWT fetch.
const hasTenantAndAuthUser = () => {
  const { tenantId, authUserId } = getUserObjectFromSessionStorage();
  return Boolean(tenantId && authUserId);
};

const setUserDataInSessionStorage = ({
  access_token: accessToken, username = null, brandPicUrl = null, name = null,
  sandboxValidity = null, brandName = null, userRole = null, organizationUserId = null,
  organizationId = null,
}) => {
  setInSessionStorage(AUTH_TOKEN, accessToken);
  setInSessionStorage(USERNAME, username);
  setInSessionStorage(USER_BRAND_PIC_URL, brandPicUrl);
  setInSessionStorage(NAME, name);
  setInSessionStorage(SANDBOX_VALIDITY, sandboxValidity);
  setInSessionStorage(BRAND_NAME, brandName);
  setInSessionStorage(USER_ROLE, userRole);
  setInSessionStorage(ORGANIZATION_USER_ID, organizationUserId);
  setInSessionStorage(ORGANIZATION_ID, organizationId);
};

const getFromSessionStorage = (key) => {
  let res;

  try {
    res = JSON.parse(sessionStorage.getItem(key));
  } catch (e) {
    logInDev(`Failed to parse value of ${key} from session storage. Exception - ${e}`);
  }

  if (!res && key === AUTH_TOKEN) {
    const currentUserData = getUserObjectFromSessionStorage();

    // De-structure current user data and set user details as flat property.
    if (currentUserData.access_token) {
      setUserDataInSessionStorage(currentUserData);
    }

    res = currentUserData.access_token || null;
  }

  return res;
};

const removeFromSessionStorage = (key) => {
  sessionStorage.removeItem(key);
};

const updateCurrentUserProperties = (data) => {
  const currentUserData = getUserObjectFromSessionStorage();
  setInSessionStorage(CURRENT_USER, { ...currentUserData, ...data });
};

const clearSessionStorage = () => {
  removeFromSessionStorage(CURRENT_USER);
  removeFromSessionStorage(AUTH_TOKEN);
  removeFromSessionStorage(USERNAME);
  removeFromSessionStorage(USER_BRAND_PIC_URL);
  removeFromSessionStorage(NAME);
  removeFromSessionStorage(SANDBOX_VALIDITY);
  removeFromSessionStorage(BRAND_NAME);
  removeFromSessionStorage(USER_ROLE);
  removeFromSessionStorage(ORGANIZATION_USER_ID);
  removeFromSessionStorage(ACTIVE_IMPERSONATOR);
  removeFromSessionStorage(MOBILE_UNSUPPORTED_ROUTES_EXCLUSION_LIST);
  removeFromSessionStorage(RAZORPAY_PROFILE_NAME);
  removeFromSessionStorage(SHOW_COLLAPSED_NAV);
  removeFromSessionStorage(JWT_TOKEN);
  removeFromSessionStorage(JWT_TOKEN_EXPIRY);
  removeFromSessionStorage(ORGANIZATION_ID);
  // Agent state cleanup
  removeFromSessionStorage(AGENT_SIDEBAR_OPEN);
  removeFromSessionStorage(AGENT_CURRENT_CONVERSATION_ID);
};

export {
  setInSessionStorage,
  getFromSessionStorage,
  removeFromSessionStorage,
  clearSessionStorage,
  updateCurrentUserProperties,
  getUserObjectFromSessionStorage,
  hasTenantAndAuthUser,
};
